/**
 * Custom-column cell for `link` columns.
 *
 * The row shows the URL (protocol stripped) as an underlined link; clicking
 * the cell opens a small popover to edit it, with an "Open link" action that
 * launches the URL in a new tab. The persisted value is the raw `string` URL;
 * clearing it stores null.
 */
import { Input, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { ExternalLink, Link2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toHref } from '../../lib/toHref';
import { EM_DASH } from './constants';

interface LinkCustomCellProps {
  value: string | undefined;
  onChange: (next: string | null) => void;
}

/** Compact display: drop the scheme and any trailing slash. */
function toLabel(value: string): string {
  return value.replace(/^[a-z][\w+.-]*:\/\//i, '').replace(/\/$/, '');
}

export function LinkCustomCell({ value, onChange }: LinkCustomCellProps) {
  const { t } = useTranslation('trackers');

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) setDraft(value ?? '');
  }, [value, open]);
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const href = value ? toHref(value) : undefined;

  const commit = (next: string) => {
    const trimmed = next.trim();
    const current = (value ?? '').trim();
    if (trimmed === current) return;
    onChange(trimmed === '' ? null : trimmed);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) commit(draft);
        setOpen(nextOpen);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="flex w-full items-center justify-center gap-1 truncate text-sm font-normal"
        >
          {value && value.length > 0 ? (
            <>
              <Link2 className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate text-primary underline underline-offset-2">
                {toLabel(value)}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">{EM_DASH}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-80 p-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-2">
          <Input
            ref={inputRef}
            value={draft}
            placeholder="https://…"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit(draft);
                setOpen(false);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setDraft(value ?? '');
                setOpen(false);
              }
            }}
          />
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 text-sm text-primary hover:underline"
            >
              <ExternalLink className="size-3.5" aria-hidden />
              {t('trackersBoard.linkCustomCell.openLink')}
            </a>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
